import { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardBody,
    CardHeader,
    Container,
    Flex,
    Heading,
    Select,
    SimpleGrid,
    Skeleton,
    Spinner,
    Text,
    useColorModeValue,
    VStack
} from '@chakra-ui/react';
import SidebarWithHeader from 'components/SidebarWithHeader';
import { useGetPagesQuery, useGetPageAnalyticsQuery } from 'store/BmAccounts/bmAccountApi';

function PageanalyticsForClient() {
    const [selectedPageId, setSelectedPageId] = useState('');

    // Fetch pages
    const { data: pagesData, isLoading: isPagesLoading, error: pagesError } = useGetPagesQuery({});

    // Auto-select first page when pages are loaded
    useEffect(() => {
        if (pagesData && pagesData.length > 0 && !selectedPageId) {
            setSelectedPageId(pagesData[0].id);
        }
    }, [pagesData, selectedPageId]);

    // Fetch page analytics when a page is selected
    const { data: analyticsData, isLoading: isAnalyticsLoading, isFetching: isAnalyticsFetching, error: analyticsError } = useGetPageAnalyticsQuery(
        selectedPageId,
        { skip: !selectedPageId }
    );

    const cardBg = useColorModeValue('white', '#1e1530');
    const borderColor = useColorModeValue('gray.200', 'inherit');
    const valueBg = useColorModeValue('#F5F3F9', '#3c326f');

    const handlePageChange = (event) => {
        setSelectedPageId(event.target.value);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <SidebarWithHeader>
            <Box>
                <VStack spacing={4} align="stretch">
                    <Heading size="lg">Page Analytics</Heading>

                    {/* Page Selector Dropdown */}
                    <Box>
                        {isPagesLoading ? (
                            <Skeleton height="32px" borderRadius="md" maxW={'300px'}/>
                        ) : pagesError ? (
                            <Text color="red.500">Error loading pages</Text>
                        ) : (
                            <Select
                                value={selectedPageId}
                                onChange={handlePageChange}
                                size="sm"
                                borderRadius={'md'}
                                maxW="300px"
                            >
                                {pagesData?.map((page) => (
                                    <option key={page.id} value={page.id}>
                                        {page.name}
                                    </option>
                                ))}
                            </Select>
                        )}
                    </Box>

                    {/* Analytics Display */}
                    {selectedPageId && (
                        <Box>
                            {isAnalyticsLoading || isAnalyticsFetching ? (
                                <Flex
                                    justify="center"
                                    align="center"
                                    minH="400px"
                                    direction="column"
                                    gap={4}
                                >
                                    <Spinner size="md"/>
                                    <Text>Loading...</Text>
                                </Flex>
                            ) : analyticsError ? (
                                <Text color="red.500">Error loading analytics</Text>
                            ) : analyticsData?.analytics ? (
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                                    {analyticsData.analytics.map((metric, index) => (
                                        <Card
                                            key={index}
                                            bg={cardBg}
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                            shadow="md"
                                        >
                                            <CardHeader pb={2}>
                                                <Heading size="md">{metric.title}</Heading>
                                                <Text fontSize="sm" color="gray.500" mt={1}>
                                                    {metric.description}
                                                </Text>
                                                <Text fontSize="xs" mt={1} fontWeight="semibold">
                                                    Period: {metric.period}
                                                </Text>
                                            </CardHeader>
                                            <CardBody pt={2}>
                                                <VStack align="stretch" spacing={2}>
                                                    {metric.values.map((value, idx) => (
                                                        <Flex
                                                            key={idx}
                                                            p={2}
                                                            bg={valueBg}
                                                            borderRadius="md"
                                                            justify="space-between"
                                                            align="center"
                                                        >
                                                            <Text fontSize="xs">
                                                                {formatDate(value.end_time)}
                                                            </Text>
                                                            <Text fontSize="xl" fontWeight="bold">
                                                                {formatNumber(value.value)}
                                                            </Text>
                                                        </Flex>
                                                    ))}
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </SimpleGrid>
                            ) : (
                                <Text>No analytics data available</Text>
                            )}
                        </Box>
                    )}
                </VStack>
            </Box>
        </SidebarWithHeader>
    );
}

export default PageanalyticsForClient;
